/* eslint-disable max-lines */
import { STANDARD_DTF_CATALOG_NUMBER } from "@design-lab/shared/constants/constants";
import type { DesignLabCtaEventName, DesignLabFormType, Side } from "@design-lab/shared/types";
import type { Artwork } from "@design-lab/shared/types/artwork";
import { buildItemIdentifiers } from "common/jiffy/get-variant";
import { sendDataLayer } from "common/utils/ga";
import { track } from "common/utils/mixpanel/tracker";
import {
  track as trackRudderstack,
  trackRudderstackImageProcessingCompleted
} from "common/utils/rudderstack/tracker";
import { getMadeProductType } from "components/jiffy/product-details/customizable-made-page-utils";
import { deepFlattenObject } from "./objectUtils";

const trackAsync = (event: string, properties: Record<string, unknown>) =>
  new Promise<void>(resolve => {
    let resolved = false;

    const safeResolve = () => {
      if (!resolved) {
        resolved = true;
        resolve();
      }
    };

    try {
      track(event, properties, safeResolve);
      setTimeout(safeResolve, 1000);
    } catch {
      safeResolve();
    }
  });

export const trackDesignLabSessionStart = () => {
  const timestamp = Date.now();

  track("design_lab_session_started", {
    timestamp
  });
};

// Enhanced interface for design lab upload events to match image_upload events
export interface DesignLabUploadEventOptions {
  placement: Side;
  source: "upload" | "design_library" | "canva" | "ai_generation";
  // File information
  file?: File;
  // File type (for design library selections where File object is not available)
  fileType?: string;
  // Business context
  catalogNumber?: string;
  businessLine?: string;
  orderType?: string;
  // Upload context
  page?: string;
  pdpType?: string;
  projectId?: string | null;
  // Artwork information
  multiArtwork?: boolean;
  artworksCount?: number;
  // Performance tracking
  uploadStartTime?: number;
  // Additional metadata
  dpi?: number;
  preCutTransfers?: boolean;
  // Upload tracking
  uploadUuid?: string | undefined;
}

export type AnalyticsClarification = {
  question: string;
  value: string;
  option_type: string;
};

export const trackDesignUploadStarted = (
  placement: Side,
  source: "upload" | "design_library" | "canva" | "ai_generation" | "camera",
  options?: Partial<DesignLabUploadEventOptions>
) => {
  const basePayload = {
    placement,
    source,
    timestamp: Date.now(),
    ...(options?.uploadUuid && { upload_uuid: options.uploadUuid })
  };

  // Add file information if available
  const filePayload = options?.file
    ? {
        file_name: options.file.name,
        file_type: options.file.type,
        file_size: options.file.size
      }
    : {
        // For design library selections, file_type may be provided directly
        ...(options?.fileType && { file_type: options.fileType })
      };

  // Add business context if available
  const businessPayload = {
    ...(options?.catalogNumber && { catalog_number: options.catalogNumber }),
    ...(options?.businessLine && { business_line: options.businessLine }),
    ...(options?.orderType && { order_type: options.orderType }),
    ...(options?.page && { page: options.page }),
    ...(options?.pdpType && { pdp_type: options.pdpType }),
    ...(options?.projectId !== undefined && { project_id: options.projectId }),
    ...(options?.multiArtwork !== undefined && { multi_artwork: options.multiArtwork }),
    ...(options?.artworksCount !== undefined && { artworks_count: options.artworksCount }),
    ...(options?.dpi && { dpi: options.dpi }),
    ...(options?.preCutTransfers !== undefined && { pre_cut_transfers: options.preCutTransfers })
  };

  track("design_lab_upload_started", {
    ...basePayload,
    ...filePayload,
    ...businessPayload
  });

  trackRudderstack(
    "Pilot: Image Upload Started",
    {
      ...basePayload,
      ...filePayload,
      ...businessPayload
    },
    true
  );
};

export const trackDesignUploadFailed = (options: {
  type: "validation_error" | "unhandled_error" | "timeout_error";
  errorMessage: string;
}) => {
  track("design_lab_upload_failed", {
    type: options.type,
    error_message: options.errorMessage,
    timestamp: Date.now()
  });
};

/* eslint-disable max-params */
export const trackDesignUploadCompleted = (
  placement: Side,
  artworkId: string,
  source: "upload" | "design_library" | "canva" | "ai_generation" | "camera",
  options?: Partial<DesignLabUploadEventOptions>
) => {
  const basePayload = {
    placement,
    artwork_id: artworkId,
    source,
    timestamp: Date.now(),
    ...(options?.uploadUuid && { upload_uuid: options.uploadUuid })
  };

  // Add file information if available
  const filePayload = options?.file
    ? {
        file_name: options.file.name,
        file_type: options.file.type,
        file_size: options.file.size
      }
    : {};

  // Add business context if available
  const businessPayload = {
    ...(options?.catalogNumber && { catalog_number: options.catalogNumber }),
    ...(options?.businessLine && { business_line: options.businessLine }),
    ...(options?.orderType && { order_type: options.orderType }),
    ...(options?.page && { page: options.page }),
    ...(options?.pdpType && { pdp_type: options.pdpType }),
    ...(options?.projectId !== undefined && { project_id: options.projectId }),
    ...(options?.multiArtwork !== undefined && { multi_artwork: options.multiArtwork }),
    ...(options?.artworksCount !== undefined && { artworks_count: options.artworksCount }),
    ...(options?.dpi && { dpi: options.dpi }),
    ...(options?.preCutTransfers !== undefined && { pre_cut_transfers: options.preCutTransfers })
  };

  // Add performance metrics if available
  const performancePayload = options?.uploadStartTime
    ? {
        upload_speed_megabits_per_second: calculateUploadSpeed(
          options.file,
          options.uploadStartTime
        )
      }
    : {};

  track("design_lab_upload_completed", {
    ...basePayload,
    ...filePayload,
    ...businessPayload,
    ...performancePayload
  });

  trackRudderstack(
    "Pilot: Image Upload Completed",
    {
      ...basePayload,
      ...filePayload,
      ...businessPayload,
      ...performancePayload
    },
    true
  );
};

// Helper function to calculate upload speed (similar to submit-dtf-events.js)
const calculateUploadSpeed = (file: File | undefined, startTime: number): number | undefined => {
  if (!file || !startTime) return undefined;

  const duration = performance.now() - startTime;
  if (duration <= 0) return undefined;

  // Convert bytes to megabits and calculate speed
  const sizeInMegabits = (file.size * 8) / (1024 * 1024);
  const speedInMegabitsPerSecond = sizeInMegabits / (duration / 1000);

  return Math.round(speedInMegabitsPerSecond * 100) / 100; // Round to 2 decimal places
};

export const trackImageEnhancementStarted = (
  artworkId: string,
  enhancementType: string,
  source?: "upload" | "design_library" | "canva" | "ai_generation" | "camera"
) => {
  track("design_lab_enhancement_started", {
    artwork_id: artworkId,
    enhancement_type: enhancementType,
    source,
    start_time: Date.now()
  });

  trackRudderstack(
    "Pilot: Image Enhancement Started",
    {
      artwork_id: artworkId,
      enhancement_type: enhancementType,
      source,
      start_time: Date.now()
    },
    true
  );
};

export const trackImageEnhancementCompleted = (options: {
  artworkId: string;
  enhancementType: string;
  startTime: number;
  source?: "upload" | "design_library" | "canva" | "ai_generation" | "camera";
  triggerEvent?: string;
}) => {
  const duration = Date.now() - options.startTime;
  track("design_lab_enhancement_completed", {
    artwork_id: options.artworkId,
    enhancement_type: options.enhancementType,
    source: options.source,
    duration_ms: duration,
    timestamp: Date.now(),
    ...(options.triggerEvent ? { trigger_event: options.triggerEvent } : {})
  });

  trackRudderstack(
    "Pilot: Image Enhancement Completed",
    {
      artwork_id: options.artworkId,
      enhancement_type: options.enhancementType,
      source: options.source,
      duration_ms: duration,
      timestamp: Date.now(),
      ...(options.triggerEvent ? { trigger_event: options.triggerEvent } : {})
    },
    true
  );
};

export const trackDesignLabImageProcessingCompleted = (artwork: Artwork, artworkId: string) => {
  trackRudderstackImageProcessingCompleted({
    artwork_id: artworkId,
    location: "design_lab",
    catalog_number: artwork.metadata?.catalogNumber || STANDARD_DTF_CATALOG_NUMBER,
    file_type: artwork.filetype,
    file_size: artwork.filesize,
    file_name: artwork.filename,
    dpi: artwork.originalDpi || artwork.originalMetadata?.dpi,
    business_line: "Print",
    artworks_count: 1,
    ...(artwork.noBgUrl || artwork.withBgUrl
      ? { enhanced_image_url: artwork.noBgUrl || artwork.withBgUrl }
      : {}),
    ...(artwork.originalImageUrl ? { original_image_url: artwork.originalImageUrl } : {})
  });
};

export const trackDesignLabEnhancementFailed = (options: {
  artworkId: string;
  errorMessage: string;
}) => {
  track("design_lab_enhancements_failed", {
    artwork_id: options.artworkId,
    error_message: options.errorMessage,
    timestamp: Date.now()
  });
};

export const trackArtworkLongProcessing = async (options: {
  artworkId: string;
  startTime: number;
  side?: Side;
  source?: "upload" | "design_library" | "canva" | "ai_generation" | "camera";
  durationMs: number;
  lastWebsocketEvent: string;
  passedSteps: string[];
  cacheWarmingStatus: "started" | "warmed" | "none";
}) => {
  let enhancementData = {};

  try {
    const api = await import("common/utils/api");
    const artworkDetails = await api.default.dtf.getArtwork(
      { id: options.artworkId },
      { catalog_number: "DTF001" }
    );
    enhancementData = {
      enhancement_status: artworkDetails.enhancement_status,
      enhancement_started_at: artworkDetails.enhancement_started_at,
      enhancement_completed_at: artworkDetails.enhancement_completed_at
    };
  } catch {
    // snooze errors
  }

  track("design_lab_long_processing", {
    artwork_id: options.artworkId,
    start_time: options.startTime,
    side: options.side,
    source: options.source,
    duration_ms: options.durationMs,
    last_websocket_event: options.lastWebsocketEvent,
    passed_steps: options.passedSteps,
    cache_warming_status: options.cacheWarmingStatus,
    ...enhancementData,
    timestamp: Date.now()
  });
};

export const trackSeeComparisonClicked = () => {
  track("design_lab_see_comparison_clicked", {
    timestamp: Date.now()
  });
};

export const trackPrintOptionClicked = (
  option: "print_as_is" | "print_perfect" | "artist_edit"
) => {
  track("design_lab_print_option_clicked", {
    print_option: option,
    timestamp: Date.now()
  });
};

export const trackZoomClicked = (zoomLevel?: number) => {
  track("design_lab_zoom_clicked", {
    zoom_level: zoomLevel,
    timestamp: Date.now()
  });
};

export const trackPlacementChanged = (fromPlacement: string, toPlacement: string) => {
  track("design_lab_placement_changed", {
    from_placement: fromPlacement,
    to_placement: toPlacement,
    timestamp: Date.now()
  });
};

export const trackChatCtaClicked = () => {
  track("design_lab_chat_cta_clicked", {
    timestamp: Date.now()
  });
};

export const trackImageDeleted = (placement: Side, artworkId?: string) => {
  track("design_lab_image_deleted", {
    placement,
    artwork_id: artworkId || "unknown",
    timestamp: Date.now()
  });
};

export const trackLearnMoreClicked = () => {
  track("design_lab_learn_more_clicked", {
    timestamp: Date.now()
  });
};

export const trackTabNavigation = (tabName: string, source: "tabs" | "next") => {
  track("design_lab_tab_clicked", {
    tab_name: tabName,
    location: "design_lab",
    source: source === "tabs" ? "tab_click" : "next_click",
    timestamp: Date.now()
  });
};

export const trackUploadTabNavigation = (tabName: "size" | "placement" | "image_enhancement") => {
  track("design_lab_upload_tab_clicked", {
    tab_name: tabName,
    timestamp: Date.now()
  });
};

export const trackProductColorChanged = (
  catalogId: string,
  fromColorName: string,
  toColorName: string
) => {
  track("design_lab_product_color_changed", {
    catalog_id: catalogId,
    from_color_name: fromColorName,
    to_color_name: toColorName,
    timestamp: Date.now()
  });
};

export const trackQuantityChanged = (options: {
  catalogId: string;
  colorName: string;
  size: string;
  quantity: number;
}) => {
  track("design_lab_quantity_changed", {
    catalog_id: options.catalogId,
    color_name: options.colorName,
    size: options.size,
    quantity: options.quantity,
    timestamp: Date.now()
  });
};

export const trackNextChooseSizesClicked = () => {
  track("design_lab_next_choose_sizes_clicked", {
    timestamp: Date.now()
  });
};

export const trackBackButtonClicked = (fromPage: string, toPage: string) => {
  track("design_lab_back_button_clicked", {
    from_page: fromPage,
    to_page: toPage,
    timestamp: Date.now()
  });
};

export const trackEnhancementProgressBarClosed = () => {
  track("design_lab_enhancement_progress_bar_closed", {
    timestamp: Date.now()
  });
};

export const trackProductAddedToDesign = (
  productType: "same_product_different_color" | "different_product",
  catalogId: string,
  colorName: string
) => {
  track("design_lab_product_added", {
    product_type: productType,
    catalog_id: catalogId,
    color_name: colorName,
    timestamp: Date.now()
  });
};

export const trackProductDeletedFromDesign = (
  catalogId: string,
  colorName: string,
  compositionId: string
) => {
  track("design_lab_product_deleted", {
    catalog_id: catalogId,
    color_name: colorName,
    composition_id: compositionId,
    timestamp: Date.now()
  });
};

export const trackDesignLabCtaClicked = (eventName: DesignLabCtaEventName) => {
  track("design_lab_cta_clicked", {
    event_name: eventName,
    timestamp: Date.now()
  });

  // Pilot: CTA Clicked for add to cart
  if (eventName === "add_to_cart") {
    trackRudderstack(
      "Pilot: CTA Clicked",
      {
        cta_type: eventName,
        timestamp: Date.now()
      },
      true
    );
  }
};

export const trackDesignLabPinsCtaClicked = (options: {
  artworkId: string;
  sessionId?: string;
}) => {
  track("design_lab_pins_cta_clicked", {
    artwork_id: options.artworkId,
    ...(options.sessionId && { session_id: options.sessionId }),
    timestamp: Date.now()
  });
};

export const trackDesignLabPinCreated = (options: {
  artworkId: string;
  sessionId: string;
  pinId: string;
}) => {
  track("design_lab_pin_created", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    pin_id: options.pinId,
    timestamp: Date.now()
  });
};

export const trackDesignLabPinMessageSubmitted = (options: {
  artworkId: string;
  sessionId: string;
  pinId: string;
  isFirstMessage: boolean;
  turnIndex: number;
  userMessagesCount: number;
  aiMessagesCount: number;
  clarifications: AnalyticsClarification[];
}) => {
  track("design_lab_pin_message_submitted", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    pin_id: options.pinId,
    is_first_message: options.isFirstMessage,
    turn_index: options.turnIndex,
    user_messages_count: options.userMessagesCount,
    ai_messages_count: options.aiMessagesCount,
    clarifications: options.clarifications,
    timestamp: Date.now()
  });
};

export const trackDesignLabPinClarificationClicked = (options: {
  artworkId: string;
  sessionId: string;
  pinId: string;
  optionValue: string;
  turnIndex: number;
  userMessagesCount: number;
  aiMessagesCount: number;
  optionType: string;
}) => {
  track("design_lab_pin_clarification_clicked", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    pin_id: options.pinId,
    option_value: options.optionValue,
    turn_index: options.turnIndex,
    user_messages_count: options.userMessagesCount,
    ai_messages_count: options.aiMessagesCount,
    option_type: options.optionType,
    timestamp: Date.now()
  });
};

export const trackDesignLabPinReady = (options: {
  artworkId: string;
  sessionId: string;
  pinId: string;
  userMessagesCount: number;
  aiMessagesCount: number;
}) => {
  track("design_lab_pin_ready", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    pin_id: options.pinId,
    user_messages_count: options.userMessagesCount,
    ai_messages_count: options.aiMessagesCount,
    timestamp: Date.now()
  });
};

export const trackDesignLabPinUnsupported = (options: {
  artworkId: string;
  sessionId: string;
  pinId: string;
  userMessagesCount: number;
  aiMessagesCount: number;
}) => {
  track("design_lab_pin_unsupported", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    pin_id: options.pinId,
    user_messages_count: options.userMessagesCount,
    ai_messages_count: options.aiMessagesCount,
    timestamp: Date.now()
  });
};

export const trackDesignLabPinningModeEntered = (options: {
  artworkId: string;
  sessionId: string;
  hasExistingPins: boolean;
  initialMessage?: string;
}) => {
  track("design_lab_pinning_mode_entered", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    has_existing_pins: options.hasExistingPins,
    ...(options.initialMessage && { initial_message: options.initialMessage }),
    timestamp: Date.now()
  });
};

export const trackPinsSessionCancelled = (options: {
  artworkId: string;
  sessionId: string;
  pinsCount: number;
  readyPinsCount: number;
}) => {
  track("pins_session_cancelled", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    pins_count: options.pinsCount,
    ready_pins_count: options.readyPinsCount,
    timestamp: Date.now()
  });
};

export const trackPinsSessionCancellationAborted = (options: {
  artworkId: string;
  sessionId: string;
  pinsCount: number;
}) => {
  track("pins_session_cancellation_aborted", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    pins_count: options.pinsCount,
    timestamp: Date.now()
  });
};

export type EscapeHatchEventOptions = {
  triggerType: "error" | "slow_response" | "long_chat" | "timer";
  pinCount: number;
  messageCount: number;
};

export const trackPinSessionEscapeHatchShown = (options: EscapeHatchEventOptions) => {
  track("pin_session_escape_hatch_shown", {
    trigger_type: options.triggerType,
    pin_count: options.pinCount,
    message_count: options.messageCount,
    timestamp: Date.now()
  });
};

export const trackPinSessionEscapeHatchClicked = (options: EscapeHatchEventOptions) => {
  track("pin_session_escape_hatch_clicked", {
    trigger_type: options.triggerType,
    pin_count: options.pinCount,
    message_count: options.messageCount,
    timestamp: Date.now()
  });
};

export const trackPinSessionEscapeHatchConfirmed = (options: EscapeHatchEventOptions) => {
  track("pin_session_escape_hatch_confirmed", {
    trigger_type: options.triggerType,
    pin_count: options.pinCount,
    message_count: options.messageCount,
    timestamp: Date.now()
  });
};

export const trackPinSessionEscapeHatchDismissed = (options: EscapeHatchEventOptions) => {
  track("pin_session_escape_hatch_dismissed", {
    trigger_type: options.triggerType,
    pin_count: options.pinCount,
    message_count: options.messageCount,
    timestamp: Date.now()
  });
};

export const trackPinSessionEscapeHatchFailed = (
  options: EscapeHatchEventOptions & { errorMessage: string }
) => {
  track("pin_session_escape_hatch_failed", {
    trigger_type: options.triggerType,
    pin_count: options.pinCount,
    message_count: options.messageCount,
    error_message: options.errorMessage,
    timestamp: Date.now()
  });
};

export const trackDesignLabPinningSessionCompleted = (options: {
  artworkId: string;
  sessionId: string;
  pinsCount: number;
  readyPinsCount: number;
  summary: string;
}) => {
  track("design_lab_pinning_session_completed", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    pins_count: options.pinsCount,
    ready_pins_count: options.readyPinsCount,
    summary: options.summary,
    timestamp: Date.now()
  });
};

export const trackDesignLabPinningSessionSummaryEdited = (options: {
  artworkId: string;
  sessionId: string;
}) => {
  track("pins_session_summary_edit_clicked", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    timestamp: Date.now()
  });
};

export const trackDesignLabPinningSessionCompletionFailed = (options: {
  artworkId: string;
  sessionId: string;
  errorMessage: string;
}) => {
  track("design_lab_pinning_session_completion_failed", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    error_message: options.errorMessage,
    timestamp: Date.now()
  });
};

export const trackDesignLabPinningInstructionsRemoved = (options: {
  artworkId: string;
  sessionId: string;
}) => {
  track("design_lab_pinning_instructions_removed", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    timestamp: Date.now()
  });
};

export const trackDesignLabPinSelected = (options: {
  artworkId: string;
  sessionId: string;
  pinId: string;
  previousPinId: string | null;
  pinState: string;
}) => {
  track("design_lab_pin_selected", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    pin_id: options.pinId,
    ...(options.previousPinId && { previous_pin_id: options.previousPinId }),
    pin_state: options.pinState,
    timestamp: Date.now()
  });
};

export const trackDesignLabPinDeleted = (options: {
  artworkId: string;
  sessionId: string;
  pinId: string;
  pinState: string;
  hadMessages: boolean;
}) => {
  track("design_lab_pin_deleted", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    pin_id: options.pinId,
    pin_state: options.pinState,
    had_messages: options.hadMessages,
    timestamp: Date.now()
  });
};

export const trackDesignLabPinDeleteFailed = (options: {
  artworkId: string;
  sessionId: string;
  pinId: string;
}) => {
  track("design_lab_pin_delete_failed", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    pin_id: options.pinId,
    timestamp: Date.now()
  });
};

export const trackDesignLabPinNeedsClarification = (options: {
  artworkId: string;
  sessionId: string;
  pinId: string;
  optionsCount: number;
  questionType: string | undefined;
  userMessagesCount: number;
  aiMessagesCount: number;
}) => {
  track("design_lab_pin_needs_clarification", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    pin_id: options.pinId,
    options_count: options.optionsCount,
    ...(options.questionType && { question_type: options.questionType }),
    user_messages_count: options.userMessagesCount,
    ai_messages_count: options.aiMessagesCount,
    timestamp: Date.now()
  });
};

export const trackDesignLabPinError = (options: {
  artworkId: string;
  sessionId: string;
  pinId: string;
  errorType: string;
  userMessagesCount: number;
  aiMessagesCount: number;
}) => {
  track("design_lab_pin_error", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    pin_id: options.pinId,
    error_type: options.errorType,
    user_messages_count: options.userMessagesCount,
    ai_messages_count: options.aiMessagesCount,
    timestamp: Date.now()
  });
};

export const trackPinsSessionEditCancelModalConfirmed = (options: {
  sessionId: string;
  newPinsCount: number;
}) => {
  track("pins_session_edit_cancel_modal_confirmed", {
    session_id: options.sessionId,
    new_pins_count: options.newPinsCount,
    timestamp: Date.now()
  });
};

export const trackPinsSessionEditCancelModalStayed = (options: {
  sessionId: string;
  newPinsCount: number;
}) => {
  track("pins_session_edit_cancel_modal_stayed", {
    session_id: options.sessionId,
    new_pins_count: options.newPinsCount,
    timestamp: Date.now()
  });
};

export const trackPinsSessionEditCancelFailed = (options: {
  sessionId: string;
  newPinsCount: number;
  errorMessage: string;
}) => {
  track("pins_session_edit_cancel_failed", {
    session_id: options.sessionId,
    new_pins_count: options.newPinsCount,
    error_message: options.errorMessage,
    timestamp: Date.now()
  });
};

export const trackResizeStarted = (options: {
  placement: Side;
  artworkId: string;
  width: number;
  height: number;
}) => {
  track("design_lab_dimensions_changed", {
    placement: options.placement,
    artwork_id: options.artworkId,
    width: options.width,
    height: options.height,
    timestamp: Date.now()
  });
};

export const trackDimensionInputChanged = (options: {
  placement: string;
  artworkId: string;
  dimension: "width" | "height";
  oldValue: number;
  newValue: number;
  triggeredByAspectRatio: boolean;
}) => {
  track("design_lab_dimension_input_changed", {
    placement: options.placement,
    artwork_id: options.artworkId,
    dimension: options.dimension,
    old_value: options.oldValue,
    new_value: options.newValue,
    triggered_by_aspect_ratio: options.triggeredByAspectRatio,
    timestamp: Date.now()
  });
};

export const trackRemoveBackgroundClicked = (options: {
  source: "canvas" | "PrintQuality";
  printQuality: "PrintPerfect";
  side: Side;
  artworkId: string;
}) => {
  track("design_lab_remove_background_clicked", {
    source: options.source,
    print_quality: options.printQuality,
    side: options.side,
    artwork_id: options.artworkId,
    timestamp: Date.now()
  });
};

export const trackChangeColorClicked = (options: {
  source: "canvas" | "footer";
  side: Side;
  artworkId: string;
}) => {
  track("design_lab_change_color_clicked", {
    source: options.source,
    side: options.side,
    artwork_id: options.artworkId,
    timestamp: Date.now()
  });
};

export const trackAddToCartError = (
  form: DesignLabFormType,
  error: string,
  errorResponse: unknown
) => {
  const data = deepFlattenObject(form);

  track("design_lab_add_to_cart_error", {
    ...data,
    error,
    error_response: errorResponse,
    timestamp: Date.now()
  });
};

export const trackCompositionValidationFailed = (options: {
  addedCompositions: any[];
  failedCompositionId: string;
  failedCompositionIndex: number;
  formErrors: any;
}) => {
  track("design_lab_add_to_cart_validation_failed", {
    added_compositions: options.addedCompositions,
    failed_composition_id: options.failedCompositionId,
    failed_composition_index: options.failedCompositionIndex,
    form_errors: options.formErrors,
    compositions_count: options.addedCompositions.length
  });
};

export const trackExpertEditAddonToggled = (options: {
  placement: Side;
  artworkId: string;
  composition_ids: string[];
  enabled: boolean;
  paid_expert_edit_addon: boolean | undefined;
  business_line: string;
  catalog_numbers: string[];
  active_config_section: "full_enhancements";
  source: "design_lab";
}) => {
  track("expert_edit_addon_toggled", {
    placement: options.placement,
    artwork_id: options.artworkId,
    composition_ids: options.composition_ids,
    enabled: options.enabled,
    paid_expert_edit_addon: options.paid_expert_edit_addon,
    business_line: options.business_line,
    catalog_numbers: options.catalog_numbers,
    active_config_section: options.active_config_section,
    source: options.source
  });
};
// DTF AI Redraw Events
export const trackDtfAiRedrawNoteSubmitted = (artworkId: number, note: string) => {
  track("dtf_ai_redraw_note_submitted", {
    artwork_id: artworkId,
    note,
    page: window.location.pathname,
    timestamp: Date.now()
  });
};

export const trackDtfAiRedrawQuestionsAnswered = (
  artworkId: number,
  questions: Record<string, string | undefined>
) => {
  track("dtf_ai_redraw_questions_answered", {
    artwork_id: artworkId,
    questions,
    page: window.location.pathname,
    timestamp: Date.now()
  });
};

export const trackDtfAiRedrawInterpretationComplete = (artworkId: number, step: string) => {
  track("dtf_ai_redraw_interpretation_complete", {
    artwork_id: artworkId,
    step,
    page: window.location.pathname,
    timestamp: Date.now()
  });
};

export const trackDtfAiRedrawCancelled = (artworkId: number, step: string) => {
  track("dtf_ai_redraw_cancelled", {
    artwork_id: artworkId,
    step,
    page: window.location.pathname,
    timestamp: Date.now()
  });
};

export const trackDtfAiRedrawInterpretationFailed = (artworkId: number, step: string) => {
  track("dtf_ai_redraw_interpretation_failed", {
    artwork_id: artworkId,
    step,
    page: window.location.pathname,
    timestamp: Date.now()
  });
};

export const handleArtworkSelect = (placement: Side, artworkId: string) => {
  track("design_lab_image_clicked", {
    placement,
    artwork_id: artworkId,
    timestamp: Date.now()
  });
};

export const trackChangeShirtButtonOpened = () => {
  track("change_shirt_button_opened", {
    timestamp: Date.now()
  });
};

export const trackChangeShirtModalClosed = () => {
  track("change_shirt_modal_closed", {
    timestamp: Date.now()
  });
};

export const trackProductAddedModalOpened = () => {
  track("product_added_modal", {
    source: "design_lab",
    timestamp: Date.now()
  });
};

export const trackProductAddedModalViewCartClicked = () =>
  trackAsync("view_cart_clicked", {
    source: "design_lab",
    timestamp: Date.now()
  });

export const trackProductAddedModalStartNewDesignClicked = () =>
  trackAsync("start_new_design_clicked", {
    source: "design_lab",
    timestamp: Date.now()
  });

export const trackAiStudioCtaClicked = (placement: Side) =>
  trackAsync("ai_studio_cta_clicked", {
    placement,
    source: "design_lab",
    timestamp: Date.now()
  });

export const trackImageEditToolOpened = (artworkId: string) => {
  track("image_edit_tool_opened", {
    page: "design_lab",
    artwork_id: artworkId,
    timestamp: Date.now()
  });
};

export const trackImageEditToolUsed = (
  artworkId: string,
  toolName: "auto_eraser" | "manual_eraser" | "negative_space",
  toolConfig: Record<string, unknown>
) => {
  track("image_edit_tool_used", {
    page: "design_lab",
    artwork_id: artworkId,
    tool_name: toolName,
    tool_config: toolConfig,
    timestamp: Date.now()
  });
};

export const trackImageEditToolUndo = (artworkId: string) => {
  track("image_edit_tool_undo", {
    page: "design_lab",
    artwork_id: artworkId,
    timestamp: Date.now()
  });
};

export const trackImageEditToolRedo = (artworkId: string) => {
  track("image_edit_tool_redo", {
    page: "design_lab",
    artwork_id: artworkId,
    timestamp: Date.now()
  });
};

export const trackImageEditToolReset = (artworkId: string) => {
  track("image_edit_tool_reset", {
    page: "design_lab",
    artwork_id: artworkId,
    timestamp: Date.now()
  });
};

export const trackImageEditToolCancelled = (artworkId: string) => {
  track("image_edit_tool_cancelled", {
    page: "design_lab",
    artwork_id: artworkId,
    timestamp: Date.now()
  });
};

export const trackImageEditToolConfirm = (artworkId: string | number, errorMessage?: string) => {
  track("image_edit_tool_confirm", {
    page: "design_lab",
    artwork_id: artworkId,
    ...(errorMessage && { error_message: errorMessage }),
    timestamp: Date.now()
  });
};

export const trackImageEditToolAiRefinementCalled = (dtfArtworkId: number, prompt: string) => {
  track("image_edit_tool_ai_refinement_called", {
    dtf_artwork_id: dtfArtworkId,
    prompt,
    timestamp: Date.now()
  });
};

export const trackImageEditToolAiRefinementGenerated = (dtfArtworkId: number, prompt: string) => {
  track("image_edit_tool_ai_refinement_generated", {
    dtf_artwork_id: dtfArtworkId,
    prompt,
    timestamp: Date.now()
  });
};

export const trackImageEditToolAiRefinementFailed = (
  dtfArtworkId: number,
  prompt: string,
  errorMessage: string
) => {
  track("image_edit_tool_ai_refinement_failed", {
    dtf_artwork_id: dtfArtworkId,
    prompt,
    error_message: errorMessage,
    timestamp: Date.now()
  });
};

export const trackImagePreviewSwitcherClicked = (
  mode: "t-shirt" | "green screen" | "chessboard" | "gray"
) => {
  track("image_preview_switcher_clicked", {
    page_type: "design_lab",
    mode,
    timestamp: Date.now()
  });
};

// Weave Design Lab Events
export const trackWeaveGenerateDesignPromptSubmitted = (prompt: string) => {
  track("weave_generate_design_prompt_submitted", {
    prompt,
    timestamp: Date.now()
  });
};

export const trackWeaveDesignsGenerated = (
  duration: number,
  prompt: string,
  designs: Array<{ url: string; style: Record<string, unknown> }>
) => {
  track("weave_designs_generated", {
    duration,
    prompt,
    designs,
    timestamp: Date.now()
  });
};

export const trackWeaveDesignGenerationFailed = (prompt: string, errorMessage: string) => {
  track("weave_design_generation_failed", {
    prompt,
    error_message: errorMessage,
    timestamp: Date.now()
  });
};

export const trackAiStudioGeneratedDesignChosen = (
  chosenDesign: { url: string; style: Record<string, unknown>; provider: string },
  prompt: string
) => {
  track("ai_studio_generated_design_chosen", {
    chosen_design: chosenDesign,
    prompt,
    provider: chosenDesign.provider,
    timestamp: Date.now()
  });
};

export const trackImageEditCrop = (options: {
  artworkId: string;
  cropArea: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  sizeBefore: {
    width: number;
    height: number;
  };
  sizeAfter: {
    width: number;
    height: number;
  };
}) => {
  track("image_edit_crop", {
    artwork_id: options.artworkId,
    crop_area: options.cropArea,
    size_before: options.sizeBefore,
    size_after: options.sizeAfter,
    timestamp: Date.now()
  });
};

export const trackCopyArtworkClicked = (fromSide: Side, toSide: Side) => {
  track("copy_artwork_clicked", {
    from_side: fromSide,
    to_side: toSide,
    source: "design_lab",
    timestamp: Date.now()
  });
};

interface AddToCartAnalyticsData {
  compositions: Array<{
    id: string;
    catalogId: string;
    colorName: string;
    variants: Record<string, number>;
  }>;
  products: any[];
  getVariantPrice: (compositionId: string, variantId: string) => string | undefined;
  getCompositionById: (id: string) => any;
  config?: { fbData?: string } | null;
  eventId: string;
}

export const buildAddToCartAnalyticsData = ({
  compositions,
  products,
  getVariantPrice,
  getCompositionById,
  config,
  eventId
}: AddToCartAnalyticsData) => {
  const itemsAdded: any[] = [];
  const ecommerceItemsAdded: any[] = [];
  const cartItems: any[] = [];
  const cartSkus: string[] = [];
  let addToCartValue = 0;

  compositions.forEach(composition => {
    Object.entries(composition.variants).forEach(([variantId, quantity]) => {
      if (typeof quantity === "number" && quantity > 0) {
        // Get the full composition data with variants
        const fullComposition = getCompositionById(composition.id);
        const variant = fullComposition?.variants.find((v: any) => v.id === variantId);

        // Get the product data to access brand information
        const productData = products.find(
          (p: any) => p.data?.catalogNumber === composition.catalogId
        )?.data;
        const brandName = productData?.brand || "Custom";
        const productName = productData?.name || composition.catalogId;

        const sku = variant?.sku;
        if (sku) {
          cartSkus.push(sku);
          cartItems.push({ product_id: sku });
        }

        const variantPriceStr = getVariantPrice(composition.id, variantId);
        const parsedPrice = variantPriceStr ? parseFloat(variantPriceStr) : 0;
        const variantPrice = Number.isNaN(parsedPrice) ? 0 : parsedPrice;
        addToCartValue += variantPrice * quantity;

        // Build detailed product name: catalogNumber + brand + productName + colorName
        const detailedProductName = `${composition.catalogId} ${brandName} ${productName} ${composition.colorName}`;

        // Use the existing helper to build item identifiers
        const { itemId, itemName } = buildItemIdentifiers(
          composition.catalogId,
          composition.colorName,
          true,
          detailedProductName
        );

        itemsAdded.push({
          id: itemId,
          name: itemName,
          brand: brandName,
          variant: composition.colorName,
          price: variantPrice,
          sku,
          quantity
        });

        ecommerceItemsAdded.push({
          item_id: itemId,
          item_name: itemName,
          item_brand: brandName,
          item_variant: composition.colorName,
          price: variantPrice,
          currency: "USD",
          size: variantId,
          quantity
        });
      }
    });
  });

  const productNames: string[] = [];
  const productCategories: string[] = [];

  compositions.forEach(composition => {
    const product = products.find(
      (p: any) => p.data?.catalogNumber === composition.catalogId
    )?.data;
    const brandName = product?.brand || "Custom";
    const catalogNumber = composition.catalogId;
    const compositionName = composition.colorName;
    const productCategory = product?.productCategory || "Print";

    productNames.push(`Custom Printed ${brandName} ${catalogNumber} ${compositionName}`);

    if (!productCategories.includes(productCategory)) {
      productCategories.push(productCategory);
    }
  });

  const fbContent = {
    content_name: productNames.join(", "),
    content_category: productCategories.join(", "),
    content_ids: cartSkus,
    content_type: "product",
    user_data: config?.fbData ? JSON.parse(config.fbData) : {}
  };

  return {
    itemsAdded,
    ecommerceItemsAdded,
    cartItems,
    cartSkus,
    addToCartValue,
    fbContent,
    eventId
  };
};

export const sendAddToCartAnalytics = (
  analyticsData: ReturnType<typeof buildAddToCartAnalyticsData>,
  isSample: boolean = false
) => {
  const { itemsAdded, ecommerceItemsAdded, cartItems, addToCartValue, fbContent, eventId } =
    analyticsData;

  const madeProductType = getMadeProductType(isSample);

  sendDataLayer({
    event: "AddToCart",
    cartItems,
    itemsAdded,
    addToCartValue,
    fbContent,
    addToCartEventId: eventId,
    isMadeProduct: true,
    madeProductType,
    ecommerce: {
      value: addToCartValue,
      items: ecommerceItemsAdded
    }
  });
};

/**
 * Track when AI image refinement is called
 */
export const trackAiImageRefinementCalled = (aiImageVersionId: number, prompt: string) => {
  track("ai_image_refinement_called", {
    ai_image_version_id: aiImageVersionId,
    prompt,
    timestamp: Date.now()
  });
};

/**
 * Track when AI image refinement is successfully generated
 */
export const trackAiImageRefinementGenerated = (aiImageVersionId: number, prompt: string) => {
  track("ai_image_refinement_generated", {
    ai_image_version_id: aiImageVersionId,
    prompt,
    timestamp: Date.now()
  });
};

/**
 * Track when AI image refinement fails
 */
export const trackAiImageRefinementFailed = (
  aiImageVersionId: number,
  prompt: string,
  errorMessage: string
) => {
  track("ai_image_refinement_failed", {
    ai_image_version_id: aiImageVersionId,
    prompt,
    error_message: errorMessage,
    timestamp: Date.now()
  });
};

/**
 * Track when AI image refinement undo is triggered
 */
export const trackAiImageRefinementUndo = (aiImageVersionId: number) => {
  track("ai_image_refinement_undo", {
    ai_image_version_id: aiImageVersionId,
    timestamp: Date.now()
  });
};

/**
 * Track when AI image refinement redo is triggered
 */
export const trackAiImageRefinementRedo = (aiImageVersionId: number) => {
  track("ai_image_refinement_redo", {
    ai_image_version_id: aiImageVersionId,
    timestamp: Date.now()
  });
};

export const trackImageEditToolCursorModeSelected = (mode: "cursor" | "pan" | "crop") => {
  track("image_edit_tool_cursor_mode_selected", {
    mode,
    timestamp: Date.now()
  });
};

/**
 * Track when a tool usage triggers a runtime error
 */
export const trackImageEditToolError = (
  toolName: string,
  errorMessage: string,
  options?: {
    error?: unknown;
    artworkId?: string | number;
    component?: string;
  }
) => {
  track("image_edit_tool_error", {
    tool_name: toolName,
    error_message: errorMessage,
    ...(options?.artworkId && { artwork_id: options.artworkId }),
    timestamp: Date.now()
  });

  const error = options?.error;
  let errorObject: string | undefined;
  if (error instanceof Error) {
    errorObject = error.toString();
  } else if (error) {
    errorObject = String(error);
  }
  const stackTrace = error instanceof Error ? error.stack : null;

  window.Honeybadger?.notify("Image edit tool error", {
    params: {
      tool_name: toolName,
      error_message: errorMessage,
      error_object: errorObject,
      stack_trace: stackTrace,
      ...(options?.artworkId && { artwork_id: options.artworkId }),
      ...(options?.component && { component: options.component }),
      error_type: "image_edit_tool_error"
    }
  });
};

// Pins Confirmation Modal Events
export const trackPinsConfirmationModalShown = (options: {
  frontArtworkId?: string;
  backArtworkId?: string;
}) => {
  track("pins_confirmation_modal_shown", {
    front_artwork_id: options.frontArtworkId,
    back_artwork_id: options.backArtworkId,
    timestamp: Date.now()
  });
};

export const trackPinsConfirmationEditClicked = (options: {
  artworkId: string;
  side: "front" | "back";
}) => {
  track("pins_confirmation_edit_clicked", {
    artwork_id: options.artworkId,
    side: options.side,
    timestamp: Date.now()
  });
};

export const trackPinsConfirmationDeleteClicked = (options: {
  artworkId: string;
  side: "front" | "back";
}) => {
  track("pins_confirmation_delete_clicked", {
    artwork_id: options.artworkId,
    side: options.side,
    timestamp: Date.now()
  });
};

export const trackPinsConfirmationModalClosed = () => {
  track("pins_confirmation_modal_closed", {
    timestamp: Date.now()
  });
};

export const trackPinsConfirmationAddToCartClicked = () => {
  track("pins_confirmation_add_to_cart_clicked", {
    timestamp: Date.now()
  });
};

export const trackPinningSessionChatOpened = (options: {
  sessionId: string;
  artworkId: string;
  pinsCount: number;
  lastPinState: string | null;
}) => {
  track("pinning_session_chat_opened", {
    session_id: options.sessionId,
    artwork_id: options.artworkId,
    pins_count: options.pinsCount,
    last_pin_state: options.lastPinState,
    timestamp: Date.now()
  });
};

export const trackPinningSessionEntryInput = (options: {
  artworkId: string;
  placement: string;
  inputValue: string;
}) => {
  track("pinning_session_entry_input", {
    artwork_id: options.artworkId,
    placement: options.placement,
    input: options.inputValue,
    timestamp: Date.now()
  });
};

export const trackPinSessionsGenPreviewBeforeClicked = (options: {
  sessionId: string;
  artworkId: string;
  generatedImageUrl: string;
}) => {
  track("pin_sessions_gen_preview_before_clicked", {
    session_id: options.sessionId,
    artwork_id: options.artworkId,
    generated_image_url: options.generatedImageUrl,
    timestamp: Date.now()
  });
};

export const trackPinSessionsGenConfirmed = (options: {
  artworkId: string;
  sessionId: string;
  pinId: string;
}) => {
  track("pin_sessions_gen_confirmed", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    pin_id: options.pinId,
    timestamp: Date.now()
  });
};

export const trackPinSessionsGenPreviewAfterClicked = (options: {
  sessionId: string;
  artworkId: string;
  generatedImageUrl: string;
}) => {
  track("pin_sessions_gen_preview_after_clicked", {
    session_id: options.sessionId,
    artwork_id: options.artworkId,
    generated_image_url: options.generatedImageUrl,
    timestamp: Date.now()
  });
};

export const trackPinSessionsGenDiscarded = (options: {
  artworkId: string;
  sessionId: string;
  pinId: string;
}) => {
  track("pin_sessions_gen_discarded", {
    artwork_id: options.artworkId,
    session_id: options.sessionId,
    pin_id: options.pinId,
    timestamp: Date.now()
  });
};

export const trackPinSessionGeneratedImageShown = (options: {
  sessionId: string;
  artworkId: string;
  generatedImageUrl: string;
  pinId: string;
}) => {
  track("pin_session_generated_image_shown", {
    session_id: options.sessionId,
    artwork_id: options.artworkId,
    generated_image_url: options.generatedImageUrl,
    pin_id: options.pinId,
    timestamp: Date.now()
  });
};

export const trackPriceExposed = (options: {
  priceShown: number;
  component: string;
  step: "PLP" | "DesignLab" | "Subcart" | "Checkout";
  catalogNumber: string | undefined;
  quantity: number;
  businessLine: string;
}) => {
  track("price_exposed", {
    price_shown: options.priceShown,
    component: options.component,
    step: options.step,
    catalog_number: options.catalogNumber,
    quantity: options.quantity,
    business_line: options.businessLine
  });
};
